import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, ApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/dates";
import { AuthLayout, Field } from "./AuthLayout";

// Wachtwoord instellen via de link uit een uitnodiging of reset (contract §6.1, §8):
// /wachtwoord#token=… Het token staat na # en komt dus nooit in een serverlog; het wordt meteen
// uit de adresbalk gehaald en alleen in de body naar de server gestuurd.

export function PasswordPage() {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "");
  const [info, setInfo] = useState<{ type: "invite" | "reset"; name: string; email: string; expiresAt: string } | null>(null);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; tone?: "yellow" | "blush" } | null>(null);

  useEffect(() => {
    window.history.replaceState(null, "", window.location.pathname);
    if (!token) {
      setFailed(true);
      setMessage({ text: "Deze link is onvolledig. Open de link uit je e-mail opnieuw.", tone: "blush" });
      return;
    }
    api<typeof info>("tokens/inspect", { body: { token }, quiet401: true })
      .then(setInfo)
      .catch((error: Error) => {
        setFailed(true);
        setMessage({ text: error.message, tone: "blush" });
      });
  }, [token]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== password2) {
      setMessage({ text: "De twee wachtwoorden zijn niet gelijk.", tone: "blush" });
      return;
    }
    setBusy(true);
    try {
      await api("tokens/consume", { body: { token, password }, quiet401: true });
      setDone(true);
      setMessage({ text: "Je wachtwoord is ingesteld. Je kan nu aanmelden." });
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : String(error), tone: "blush" });
      if (error instanceof ApiError && error.code === "token_invalid") setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const subtitle = info ? (info.type === "invite" ? "CRM · uitnodiging" : "CRM · nieuw wachtwoord") : "CRM · wachtwoord instellen";

  return (
    <AuthLayout subtitle={subtitle} message={message}>
      {info && !done && !failed && (
        <form onSubmit={submit}>
          <p className="mb-3 text-[13px] text-muted-foreground">
            {info.type === "invite" ? "Welkom" : "Nieuw wachtwoord voor"} {info.name} ({info.email}). Deze link is geldig tot {fmtDateTime(info.expiresAt)}.
          </p>
          <Field label="Nieuw wachtwoord (minstens 12 tekens)" htmlFor="password">
            <Input id="password" type="password" autoComplete="new-password" minLength={12} maxLength={128} required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Field label="Herhaal het wachtwoord" htmlFor="password2">
            <Input id="password2" type="password" autoComplete="new-password" minLength={12} maxLength={128} required value={password2} onChange={(e) => setPassword2(e.target.value)} />
          </Field>
          <div className="mt-1.5 flex justify-end">
            <Button type="submit" variant="default" disabled={busy}>
              Wachtwoord instellen
            </Button>
          </div>
        </form>
      )}
      {(done || failed) && (
        <div className="mt-1.5 flex justify-end">
          <Button asChild variant="default">
            <Link to="/login">Naar aanmelden</Link>
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
